import { ENV } from "@/constants/env";
import axios, { type AxiosInstance } from "axios";

// API 응답 타입 정의
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

/**
 * Axios 인스턴스 생성 - 쿠키 기반 인증
 *
 * @author Max <sud665@ymjlab.com>
 * @version 2.0.0
 * @since 2025-06-02 - 쿠키 방식으로 변경
 */
const instance: AxiosInstance = axios.create({
  baseURL: `${ENV.API_URL}`,
  withCredentials: true, // 🔥 쿠키 자동 포함
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
});

/**
 * 인증이 필요 없는 요청인지 확인
 * @param url 
 * @returns 
 */
const isPublicRequest = (url: string): boolean => {
  const publicEndpoints = [
    '/auth/login',
    '/auth/refresh'
  ];
  
  return publicEndpoints.some(endpoint => 
    url.includes(endpoint)
  );
};

// 🔥 요청 인터셉터 - 쿠키 방식에서는 별도 토큰 설정 불필요
instance.interceptors.request.use(
  async (config) => {
    console.log(`🚀 슈퍼어드민 API 요청: ${config.method?.toUpperCase()} ${config.url}`);
    
    // 쿠키는 자동으로 포함되므로 별도 헤더 설정 불필요
    return config;
  },
  (error) => {
    console.error(`❌ 슈퍼어드민 요청 에러:`, error);
    return Promise.reject(error);
  }
);

// 🔥 응답 인터셉터 - 401 에러 시 토큰 갱신 및 로그인 페이지 리다이렉트
instance.interceptors.response.use(
  (response) => {
    console.log(`✅ 슈퍼어드민 API 응답: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response.data as any; // 인터셉터에서 response.data 반환
  },
  async (error) => {
    const originalRequest = error.config;
    
    console.error(`❌ 슈퍼어드민 응답 에러: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url} - ${error.response?.status}`);

    // 🔥 401 Unauthorized 에러 처리
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // 🔥 공개 엔드포인트가 아닌 경우에만 토큰 갱신 시도
      if (!isPublicRequest(originalRequest.url)) {
        console.warn("🚪 슈퍼어드민 인증 실패 - 토큰 갱신 시도");
        
        try {
          console.log("🔄 슈퍼어드민 토큰 갱신 시도");
          
          // 🔥 올바른 admin 토큰 갱신 엔드포인트 사용
          const refreshResponse = await axios.post(
            `${ENV.API_URL}/${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/auth/refresh`,
            {},
            { 
              withCredentials: true,
              timeout: 10000 // 10초 타임아웃
            }
          );
          
          console.log("✅ 슈퍼어드민 토큰 갱신 성공:", refreshResponse.status);
          
          // 🔥 원본 요청 재시도
          return instance(originalRequest);
        } catch (refreshError) {
          console.error("❌ 슈퍼어드민 토큰 갱신 실패:", refreshError);
          
          // 🔥 authStore 상태 초기화
          try {
            const { useAuthStore } = await import('@/store');
            const { reset } = useAuthStore.getState();
            reset();
            console.log("🧹 슈퍼어드민 인증 상태 초기화 완료");
          } catch (storeError) {
            console.error("❌ 슈퍼어드민 상태 초기화 실패:", storeError);
          }
          
          // 🔥 로그인 페이지로 리다이렉트 (현재 페이지가 로그인이 아닌 경우에만)
          if (typeof window !== 'undefined') {
            const currentPath = window.location.pathname;
            if (currentPath !== "/login") {
              console.log("🚫 슈퍼어드민 로그인 페이지로 리다이렉트");
              window.location.href = "/login";
            } else {
              console.log("🚫 이미 로그인 페이지에 있음 - 리다이렉트 건너뛰기");
            }
          }
          
          return Promise.reject(refreshError);
        }
      }
    }

    // 🔥 다른 에러들은 그대로 전달
    return Promise.reject(error.response?.data || error);
  }
);

export default instance;
